// =============================================================================
// io/sync/saveAdherenceMark.ts — "did I follow this rule on this trade?"
// -----------------------------------------------------------------------------
// One click, one trade, one rule. It is the smallest write in the client and the
// one most likely to be made fifty times in a row, so everything below is about
// making a fast, repeated write safe rather than fast.
//
// ── 🚨 HYDRATE, THEN EDIT THAT OBJECT (D46) ──
//
// The Coach's Report reads eighteen columns. `tradeToDb` writes forty-eight,
// unconditionally — it cannot express "leave the other thirty alone". So marking
// a trade from the row already on screen would blank the trader's notes,
// screenshots, tags, discipline flags and fills, in exchange for one boolean.
//
// The read costs one request per click. That request IS the safety property, and
// the projection taint makes the shortcut throw rather than succeed.
//
// ── 🚨 THE MARK IS A JUDGEMENT, AND AN ABSENT ONE IS NOT A "NO" ──
//
// Adherence is computed only over trades the trader actually marked. So an
// un-mark must DELETE the key, never store `false`: a stored false counts
// against them in the verdict, and "I never judged this trade" would become "I
// broke the rule here."
//
// ⚠ MERGED PER RULE. Several rules run at once and each keeps its own answer on
// the same trade, so the map is edited key by key. Replacing it would clear the
// marks belonging to every other rule — the same failure the discipline flags
// have, one level down.
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import type { Trade } from '../../domain/types';
import { marksAfterSetting, type AdherenceTrade } from '../../domain/leakRules';
import { loadFullTrades } from './load';
import { tradeToDb } from './serialize';
import { createHydrationLedger } from './hydration';
import { reportFailure, reportFailureAs } from '../diagnostics/errorLog';

export interface SaveMarkResult {
  /** The marks now stored on the trade, so a caller can repaint without re-reading. */
  marks: Record<string, unknown> | null;
  error: string | null;
}

/**
 * Mark one trade against one rule.
 *
 * @param followed `true` held it, `false` broke it, `null` clears the judgement.
 */
export async function saveAdherenceMark(
  sb: SupabaseClient,
  tradeId: string,
  ruleId: string,
  followed: boolean | null,
  userId: string,
): Promise<SaveMarkResult> {
  if (!tradeId || !ruleId) {
    return { marks: null, error: 'Nothing to mark — no trade or no rule.' };
  }

  // ⚠ A FRESH LEDGER per call. It exists to record that THIS object was loaded
  // in full; reusing a long-lived one across a session would let a trade loaded
  // narrowly somewhere else pass the check that stops a partial save.
  const ledger = createHydrationLedger();

  let full: Trade | undefined;
  try {
    const rows = await loadFullTrades(sb, [tradeId], ledger);
    full = rows[0];
  } catch (e) {
    return {
      marks: null,
      error: `Couldn't load the trade in full, so the mark wasn't saved: `
        + `${reportFailure('save rule mark', e)}`,
    };
  }
  if (!full) {
    return {
      marks: null,
      error: `That trade no longer exists — it may have been deleted in another `
        + `tab. Nothing was saved.`,
    };
  }

  const next: Trade = { ...full };
  const t = next as unknown as Record<string, unknown>;
  const marks = marksAfterSetting(full as unknown as AdherenceTrade, ruleId, followed);
  t['ruleAdherences'] = marks;
  // ⚠ The pre-multi single mark is DELETED here and only here — `marksAfterSetting`
  // has already folded it into the map above. Leaving it would give one trade two
  // answers, and the read prefers the map, so the stale one would sit there
  // invisible until an older client read it back.
  delete t['ruleAdherence'];

  let row: Record<string, unknown>;
  try {
    row = tradeToDb(next, userId, ledger) as unknown as Record<string, unknown>;
  } catch (e) {
    return {
      marks: null,
      error: `Refusing to save: ${reportFailure('save rule mark', e)}`,
    };
  }

  // ⚠ UPDATE by id, and the returned rows are counted. An upsert on a stale id
  // would INSERT a phantom trade carrying forty-eight columns of defaults; an
  // update that matched nothing is silent unless somebody looks.
  const { data, error } = await sb
    .from('trades')
    .update(row)
    .eq('id', tradeId)
    .select('id');

  if (error) {
    return {
      marks: null,
      error: reportFailureAs(
        'save rule mark', error, `Couldn't save the mark: ${error.message}`,
      ),
    };
  }
  if (!Array.isArray(data) || data.length === 0) {
    return {
      marks: null,
      error: `The mark matched no rows, so nothing changed. The trade may have `
        + `been deleted, or it belongs to another user.`,
    };
  }

  return { marks, error: null };
}
